import {
  AvatarSize,
  DropdownAnchor,
  IconCaret,
  NestedMenuOrientation,
  Portal,
  SelectType,
  SingleSelectField,
} from '@bimone/bimtrackreactcomponents';
import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useSession } from '../../modules/session';
import { useNavBarDropDownMenu } from './hooks/useNavBarDropDownMenu';
import { useNavBarFeatureSelector } from './hooks/useNavBarFeatureSelector';
import { useNavBarHubSelector } from './hooks/useNavBarHubSelector';
import { useNavBarProjectSelector } from './hooks/useNavBarProjectSelector';
import { useNavBarRouting } from './hooks/useNavBarRouting';
import { StyledNavBar } from './styles';

export interface NavBarProps {
  showHubSelection?: boolean;
  showProjectSelection?: boolean;
  isNavBarRoutingEnabled?: boolean;
  featurePaths?: string[];
  errorUrl?: string;
}

export default function NavBar({
  showHubSelection = true,
  showProjectSelection = true,
  isNavBarRoutingEnabled = false,
  featurePaths = [],
  errorUrl = '/',
}: NavBarProps): JSX.Element {
  const {
    Container,
    Entities: { Container: Entities, Chevron, SelectorContainer, Spinner },
    User: { Container: User, Avatar, DropdownMenu, DropdownContainer },
  } = StyledNavBar;

  isNavBarRoutingEnabled ||= featurePaths?.length > 0;

  const { t } = useTranslation('navBar');
  const session = useSession();
  const { colors } = useTheme();
  const isHubSelectionEnabled = showHubSelection;
  const isProjectSelectionEnabled = showHubSelection && showProjectSelection;

  const {
    states: { selectedFeatureOption, featureOptions },
    actions: { handleOnFeatureChange, resetFeatureSelection },
  } = useNavBarFeatureSelector({
    isNavBarRoutingEnabled,
    features: featurePaths,
  });

  const {
    states: { isReady },
    actions: { handleOnHubsSettled, handleOnProjectsSettled },
  } = useNavBarRouting({
    isNavBarRoutingEnabled,
    isHubSelectionEnabled,
    isProjectSelectionEnabled,
    errorUrl,
  });

  const {
    states: { hubOptions, selectedHubOption, isLoading: isLoadingHubs },
    actions: { handleOnSelectHubOption },
  } = useNavBarHubSelector({
    isHubSelectionEnabled,
    isNavBarRoutingEnabled,
    isReady,
    onSelection: resetFeatureSelection,
    onSettled: handleOnHubsSettled,
  });

  const {
    states: { projectOptions, selectedProjectOption, isLoading: isLoadingProjects },
    actions: { handleOnSelectProjectOption },
  } = useNavBarProjectSelector({
    isProjectSelectionEnabled,
    isNavBarRoutingEnabled,
    isReady,
    onSelection: resetFeatureSelection,
    onSettled: handleOnProjectsSettled,
  });

  const {
    states: { user, options, triggerButtonRef, dropdownMenuContainerRef, open, position },
    actions: { openDropdown, closeDropdown },
  } = useNavBarDropDownMenu();

  return (
    <Container>
      <Entities>
        {showHubSelection && (
          <>
            <SelectorContainer data-testid='hub-selector'>
              <SingleSelectField
                editing
                onChange={handleOnSelectHubOption}
                options={hubOptions ?? []}
                placeholder={t('selectHub') ?? ''}
                searchable={false}
                selectType={SelectType.SUBTLE}
                value={selectedHubOption}
              />
            </SelectorContainer>
            {isLoadingHubs ? (
              <Spinner size={16} />
            ) : (
              session.hub &&
              showProjectSelection && (
                <>
                  <Chevron>
                    <IconCaret
                      direction={'Right'}
                      fill={colors.grey['600']}
                      size={16}
                    />
                  </Chevron>
                  <SelectorContainer data-testid='project-selector'>
                    <SingleSelectField
                      editing
                      onChange={handleOnSelectProjectOption}
                      options={projectOptions ?? []}
                      placeholder={t('selectProject') ?? ''}
                      searchable={false}
                      selectType={SelectType.SUBTLE}
                      value={selectedProjectOption}
                    />
                  </SelectorContainer>
                  {isLoadingProjects && <Spinner size={16} />}
                </>
              )
            )}
          </>
        )}
        {featurePaths.length > 0 && selectedProjectOption && (
          <>
            <Chevron>
              <IconCaret
                direction={'Right'}
                fill={colors.grey['600']}
                size={16}
              />
            </Chevron>
            <SelectorContainer data-testid='feature-selector'>
              <SingleSelectField
                editing
                onChange={handleOnFeatureChange}
                options={featureOptions}
                placeholder={t('feature') ?? ''}
                searchable={false}
                selectType={SelectType.SUBTLE}
                value={selectedFeatureOption}
              />
            </SelectorContainer>
          </>
        )}
      </Entities>
      <User
        onClick={open ? closeDropdown : openDropdown}
        ref={triggerButtonRef as RefObject<HTMLDivElement> | undefined}
      >
        <Avatar
          displayName={`${user?.profile.firstName} ${user?.profile.lastName}`}
          email={user?.email}
          imageUrl={user?.profile.avatarImage}
          size={AvatarSize.MEDIUM}
        />
        {open && (
          <Portal>
            <DropdownContainer
              ref={dropdownMenuContainerRef}
              x={position.left}
              y={position.top}
            >
              <DropdownMenu
                closeOnOptionSelection
                darkMode={false}
                fixedPosition
                nestedMenuOrientation={NestedMenuOrientation.RIGHT}
                open={open}
                options={options}
                orientation={DropdownAnchor.BOTTOM_RIGHT}
              />
            </DropdownContainer>
          </Portal>
        )}
      </User>
    </Container>
  );
}
